import type { GA4AnalyticsEvent } from '@/domain/shared/types';
import type {
  AncillariesAddedAndRemovedEventProps,
  AncillariesAvailableEventProps,
  AncillariesSelectedEventProps,
  CheckoutEventProps,
} from '@/scopes/checkout/domain/analytics/types';

export function createAncillariesAvailableEvent({ result }: AncillariesAvailableEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ancillaries',
    description: 'ancillary available',
    element: 'element available',
    result,
    interaction: false,
  };
}

export function createAncillariesClickedEvent({
  result,
  type,
  description,
  element,
  interaction = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ancillaries',
    description,
    element,
    result,
    interaction,
    type,
  };
}

export function createGenericServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Generic Service',
    description,
    element,
    result,
    interaction: true,
    type,
  };
}

export function createSkiServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ski',
    description,
    element,
    result,
    interaction: true,
    type,
  };
}

export function createClothingServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Clothing',
    description,
    element,
    result,
    interaction: true,
    type,
  };
}

export function createCarEvent({
  result,
  type,
  description,
  element,
  interaction = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Car',
    description,
    element,
    result,
    interaction,
    type,
  };
}

export function createEquipmentSelectionEvent({
  result,
  type,
  description,
  element,
  validated = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Equipment Selection',
    description,
    element,
    result,
    validated,
    type,
  };
}

function getDaysBeforeDeparture(departureDate: string) {
  return Math.floor((new Date(departureDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export function createCheckoutEvent(data: CheckoutEventProps) {
  const {
    step,
    userId,
    accoId,
    accoName,
    accoBrand,
    breadcrumbs,
    accoStars,
    transportType,
    boardType,
    duration,
    rating,
    partyComposition,
    totalGuests,
    departureDate,
    price,
    countryId,
    roomType,
    badges,
  } = data;

  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate);

  return {
    userId,
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step,
        },
        products: [
          {
            id: accoId,
            name: accoName,
            brand: accoBrand,
            category: breadcrumbs,
            quantity: totalGuests,
            price,
            dimension2: accoStars,
            dimension3: countryId,
            dimension4: transportType,
            dimension5: partyComposition,
            dimension6: badges,
            dimension7: rating,
            dimension8: duration,
            dimension9: partyComposition,
            dimension10: daysBeforeDeparture,
            dimension11: totalGuests,
            dimension13: departureDate,
            dimension28: roomType,
            dimension38: boardType,
          },
        ],
      },
    },
    ...(data && mapExtraData(data)),
  };
}

function mapExtraData(data: CheckoutEventProps) {
  if (!data) return {};
  const {
    accoId,
    accoBrand,
    partyComposition,
    totalGuests,
    departureDate,
    mandatoryPayment,
    accoName,
    breadcrumbs,
    accoStars,
    transportType,
    boardType,
    duration,
    rating,
    price,
    totalStepPrice,
    campaignId,
  } = data;

  return {
    accoId,
    accoBrand,
    partyComposition,
    totalGuests,
    departureDate,
    mandatoryPayment,
    accoName,
    accoCategory: breadcrumbs,
    accoStars,
    transport: transportType,
    boardType,
    duration,
    rating,
    entryPrice: price,
    totalStepPrice,
    campaignId,
  };
}

export function createAncillariesAddRemoveEvent(
  { id, name, price, category, quantity, serviceType, extraProperties }: AncillariesAddedAndRemovedEventProps,
  eventName: string,
  operationName: string,
  checkoutEventProps?: CheckoutEventProps
): GA4AnalyticsEvent {
  return {
    event: eventName,
    ecommerce: {
      [operationName]: {
        actionField: {
          list: `checkout${checkoutEventProps?.step}`,
        },
        products: [
          {
            id,
            name,
            price,
            category,
            dimension75: serviceType,
            quantity,
            ...extraProperties,
          },
        ],
      },
    },
    ...(checkoutEventProps && mapExtraData(checkoutEventProps)),
  };
}
